<template>
  <div
    class="extended-trial-balance-report"
    :class="{ 'etb-fgs-print-report': isFgsPrintMode }"
  >
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0 mr-2">{{ reportTitleText }}</h4>
        <b-badge v-show="!loading" variant="light-info">
          {{ monthWiseSummaryLabel }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">
        Calculating balances and generating report...
      </p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else>
      <!-- myskoolfgs: up to 2 months per row -->
      <div
        v-if="isFgsMonthWiseLayout"
        class="report-page etb-fgs-layout"
      >
        <div class="report-header-modern mb-4">
          <div class="d-flex align-items-center">
            <div class="logo-wrapper mr-3" v-if="schoolLogo">
              <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
            </div>
            <div class="header-text-block">
              <h2 class="school-name-full mb-0">
                {{ campusName
                }}<span v-if="campusBranch" class="text-muted">
                  - {{ campusBranch }}</span
                >
              </h2>
              <div class="report-info-line mt-1">
                <span class="report-name-text">{{ reportTitleText }}</span>
                <span class="date-range-text ml-2">
                  <strong>{{ fgsGlobalPeriodLabel }}</strong>
                </span>
                <b-badge
                  v-if="fgsIsBalanced"
                  variant="light-success"
                  class="ml-1"
                >
                  Balanced
                </b-badge>
                <b-badge v-else variant="light-danger" class="ml-1">
                  Unbalanced
                </b-badge>
              </div>
            </div>
          </div>
        </div>

        <etb-fgs-spreadsheet-table
          v-if="fgsPivotItems.length"
          :items="fgsPivotItems"
          :month-keys="fgsMonthKeys"
          :totals="fgsTotals"
          :sections="fgsSections"
          :grand-total="fgsGrandTotal"
          :imbalances="fgsImbalances"
          :is-balanced="fgsIsBalanced"
          @open-ledger="openLedger"
        />
        <div v-else class="etb-empty-month py-4 text-center">
          <p class="text-muted mb-0">No accounts for the selected period.</p>
        </div>

        <div class="page-footer">
          <small
            >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
          >
        </div>
      </div>

      <div
        v-for="(section, sectionIdx) in reportSections"
        v-else
        :key="'etb-section-' + sectionIdx"
        class="report-page"
        :class="{ 'etb-month-section': monthWise && sectionIdx > 0 }"
      >
        <!-- Header Section -->
        <div class="report-header-modern mb-4">
          <div class="d-flex align-items-center">
            <div class="logo-wrapper mr-3" v-if="schoolLogo">
              <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
            </div>
            <div class="header-text-block">
              <h2 class="school-name-full mb-0">
                {{ campusName
                }}<span v-if="campusBranch" class="text-muted">
                  - {{ campusBranch }}</span
                >
              </h2>
              <div class="report-info-line mt-1">
                <span class="report-name-text">{{ reportTitleText }}</span>
                <span
                  v-if="!monthWise"
                  class="date-range-text ml-2"
                >
                  <strong>{{ formatDate(dtFrom) }}</strong> to
                  <strong>{{ formatDate(dtTo) }}</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="monthWise && section.month"
          class="etb-month-banner mb-3"
        >
          <h3 class="etb-month-banner__title mb-0">
            {{ formatEtbMonthLabel(section.month) }} {{ section.year }}
          </h3>
          <p
            v-if="section.fromDate && section.toDate"
            class="etb-month-banner__range mb-0"
          >
            {{ formatDate(section.fromDate) }}
            <span class="mx-50">–</span>
            {{ formatDate(section.toDate) }}
          </p>
          <small class="etb-month-banner__meta text-muted">
            {{ formatNumber(section.items.length) }}
            {{ section.items.length === 1 ? "account" : "accounts" }}
          </small>
        </div>

        <!-- Main Ledger Table -->
        <div v-if="!section.items.length" class="etb-empty-month py-4 text-center">
          <p class="text-muted mb-0">No accounts for this month.</p>
        </div>
        <div v-else class="etb-table-wrap">
          <table class="etb-table">
          <thead>
            <tr class="main-header">
              <th rowspan="2" class="col-account">ACCOUNT DETAILS</th>
              <th colspan="2" class="text-center col-group">OPENING BALANCE</th>
              <th colspan="2" class="text-center col-group">
                CURRENT ACTIVITY
              </th>
              <th colspan="2" class="text-center col-group">CLOSING BALANCE</th>
            </tr>
            <tr class="sub-header">
              <th class="text-right bg-opening col-amount">Dr.</th>
              <th class="text-right bg-opening col-amount">Cr.</th>
              <th class="text-right bg-activity col-amount">Dr.</th>
              <th class="text-right bg-activity col-amount">Cr.</th>
              <th class="text-right bg-closing col-amount">Dr.</th>
              <th class="text-right bg-closing col-amount">Cr.</th>
            </tr>
          </thead>
          <!-- We use multiple tbody elements for grouping -->
          <tbody
            v-for="(natureGroup, nature) in groupItemsByNature(section.items)"
            :key="sectionIdx + '-' + nature"
          >
            <!-- Nature Header (e.g., ASSET, LIABILITY) -->
            <tr class="nature-header">
              <td colspan="7">{{ nature }}</td>
            </tr>

            <!-- Account Rows -->
            <tr
              v-for="(item, idx) in natureGroup"
              :key="nature + '-' + idx"
              :class="getRowClass(item)"
            >
              <td
                class="account-cell"
                :style="{
                  paddingLeft:
                    (item.level > 1 ? item.level - 1 : 0) * 25 + 15 + 'px',
                }"
                :class="{
                  'is-leaf': !item.isParent,
                  'is-parent': item.isParent,
                  'lvl-1': item.level === 1,
                  'lvl-2': item.level === 2,
                }"
              >
                <div
                  class="tree-line"
                  v-if="item.level > 1"
                  :style="{ left: (item.level - 2) * 25 + 30 + 'px' }"
                ></div>
                <a
                  href="#"
                  class="account-link"
                  @click.prevent="openLedger(item, section)"
                >
                  <span v-if="item.accountCode" class="account-code">{{
                    item.accountCode
                  }}</span>
                  <span class="account-title">{{ item.accountTitle }}</span>
                </a>
              </td>

              <!-- Opening -->
              <td class="text-right amount-cell">
                {{ formatAmount(item.openingDebit) }}
              </td>
              <td class="text-right amount-cell">
                {{ formatAmount(item.openingCredit) }}
              </td>

              <!-- Activity -->
              <td class="text-right amount-cell">
                {{ formatAmount(item.activityDebit) }}
              </td>
              <td class="text-right amount-cell">
                {{ formatAmount(item.activityCredit) }}
              </td>

              <!-- Closing -->
              <td class="text-right amount-cell font-weight-bold">
                {{ formatAmount(item.closingDebit) }}
              </td>
              <td class="text-right amount-cell font-weight-bold">
                {{ formatAmount(item.closingCredit) }}
              </td>
            </tr>

            <!-- Nature Footer Totals -->
            <tr class="nature-footer">
              <td class="text-right font-weight-bold">TOTAL {{ nature }}</td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    getNatureTotal(nature, "openingDebit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    getNatureTotal(nature, "openingCredit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    getNatureTotal(nature, "activityDebit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    getNatureTotal(nature, "activityCredit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    getNatureTotal(nature, "closingDebit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    getNatureTotal(nature, "closingCredit", section.items)
                  )
                }}
              </td>
            </tr>
          </tbody>

          <!-- Grand Total Footer -->
          <tbody class="grand-total-tbody">
            <tr class="grand-total-row">
              <td class="text-right font-weight-bold uppercase">GRAND TOTAL</td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    totalGrand("openingDebit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    totalGrand("openingCredit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    totalGrand("activityDebit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold">
                {{
                  formatCurrency(
                    totalGrand("activityCredit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold total-highlight">
                {{
                  formatCurrency(
                    totalGrand("closingDebit", section.items)
                  )
                }}
              </td>
              <td class="text-right font-weight-bold total-highlight">
                {{
                  formatCurrency(
                    totalGrand("closingCredit", section.items)
                  )
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Signature Section -->
      <!-- <div class="signature-section">
        <div class="sig-box">
          <div class="sig-line"></div>
          <span>Accounts Manager</span>
        </div>
        <div class="sig-box">
          <div class="sig-line"></div>
          <span>Internal Auditor</span>
        </div>
        <div class="sig-box">
          <div class="sig-line"></div>
          <span>Principal / Director</span>
        </div>
      </div> -->

      <!-- Footer -->
      <div class="page-footer">
        <small
          >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
        >
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import EtbFgsSpreadsheetTable from "./EtbFgsSpreadsheetTable.vue";

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  name: "ExtendedTrialBalancePrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
    EtbFgsSpreadsheetTable,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportData: [],
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      campusAddress: "",
      db: "",
      cID: "",
      dtFrom: "",
      dtTo: "",
      cashFlowCategory: "",
      monthWise: false,
      monthFrom: 0,
      monthTo: 0,
      etbYear: 0,
      monthBlocks: [],
      /** FGS spreadsheet pivot (new API shape) */
      fgsPivotItems: [],
      fgsMonthKeys: [],
      fgsTotals: null,
      fgsSections: [],
      fgsGrandTotal: null,
      fgsImbalances: [],
      fgsFromDate: "",
      fgsToDate: "",
      fgsIsBalanced: true,
      reportDomain: String(this.$store.state.report || "")
        .replace(/_/g, "")
        .toLowerCase(),
    };
  },
  computed: {
    isFgsPrintMode() {
      return this.reportDomain === "myskoolfgs" && this.monthWise;
    },
    isFgsMonthWiseLayout() {
      return this.isFgsPrintMode && this.fgsPivotItems.length > 0;
    },
    /** Ledger category from ETB filter / API (bank → BPV/BRV, cash → CPV/CRV) */
    fgsLedgerCategory() {
      const raw = String(this.cashFlowCategory || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
      if (raw === "bank" || raw === "bank_account") return "bank";
      if (raw === "cash" || raw === "cash_account") return "cash";
      return "";
    },
    fgsGlobalPeriodLabel() {
      if (this.fgsMonthKeys.length) {
        const first = this.fgsMonthKeys[0];
        const last = this.fgsMonthKeys[this.fgsMonthKeys.length - 1];
        return `For the Period from ${first} to ${last}`;
      }
      const from = this.formatEtbMonthLabel(this.monthFrom);
      const to = this.formatEtbMonthLabel(this.monthTo);
      const year = this.etbYear || "";
      if (this.monthFrom && this.monthTo) {
        if (this.monthFrom > this.monthTo) {
          return `For the Period from ${from}-${String(year).slice(-2)} to ${to}-${String(
            Number(year) + 1
          ).slice(-2)}`;
        }
        return `For the Period from ${from} – ${to} ${year}`.trim();
      }
      return year ? String(year) : "";
    },
    reportTitleText() {
      if (this.cashFlowCategory === "bank_account") {
        return "Bank Account Extended Trial Balance";
      }
      if (this.cashFlowCategory === "cash_account") {
        return "Cash Account Extended Trial Balance";
      }
      return "Extended Trial Balance Report";
    },
    filteredItems() {
      if (this.isFgsPrintMode && this.fgsPivotItems.length) {
        return this.fgsPivotItems;
      }
      if (this.monthWise && this.monthBlocks.length) {
        return this.reportSections.reduce(
          (acc, block) => acc.concat(block.items || []),
          []
        );
      }
      return this.filterItemsByEtbCategory(this.reportData || []);
    },
    reportSections() {
      if (this.monthWise && this.monthBlocks.length) {
        return this.monthBlocks.map((block) => ({
          ...block,
          items: this.filterItemsByEtbCategory(block.items),
        }));
      }
      return [
        {
          month: null,
          year: null,
          fromDate: this.dtFrom,
          toDate: this.dtTo,
          items: this.filterItemsByEtbCategory(this.reportData || []),
        },
      ];
    },
    monthWiseSummaryLabel() {
      if (this.isFgsPrintMode && this.fgsMonthKeys.length) {
        return `${this.fgsMonthKeys.length} month(s) · ${this.fgsPivotItems.length} account(s)`;
      }
      if (this.monthWise && this.reportSections.length) {
        const months = this.reportSections.length;
        const accounts = this.filteredItems.length;
        return `${months} month(s) · ${accounts} account(s)`;
      }
      return `${this.filteredItems.length} account(s)`;
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
  watch: {
    isFgsPrintMode: {
      immediate: true,
      handler(isFgs) {
        this.applyFgsPrintDocumentMode(isFgs);
      },
    },
  },
  created() {
    this.initReport();
  },
  beforeDestroy() {
    this.applyFgsPrintDocumentMode(false);
  },
  methods: {
    applyFgsPrintDocumentMode(isFgs) {
      const styleId = "etb-fgs-print-page-style";
      if (isFgs) {
        document.body.classList.add("etb-fgs-print");
        let styleEl = document.getElementById(styleId);
        if (!styleEl) {
          styleEl = document.createElement("style");
          styleEl.id = styleId;
          document.head.appendChild(styleEl);
        }
        /* Tabloid landscape (17×11 in) — avoids blank portrait page + named @page breaks */
        styleEl.textContent = `
          @page {
            size: 17in 11in;
            margin: 6mm 8mm;
          }
          @media print {
            html, body {
              width: auto !important;
              height: auto !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
            }
            body.etb-fgs-print #app,
            body.etb-fgs-print .extended-trial-balance-report {
              min-height: 0 !important;
              height: auto !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            body.etb-fgs-print .report-page.etb-fgs-layout {
              margin: 0 !important;
              padding: 0 !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              overflow: visible !important;
            }
            body.etb-fgs-print .etb-fgs-table-block {
              overflow: visible !important;
              max-width: 100% !important;
            }
            body.etb-fgs-print .etb-fgs-unified-table {
              width: 100% !important;
              max-width: 100% !important;
            }
            body.etb-fgs-print ::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
            }
            body.etb-fgs-print * {
              scrollbar-width: none !important;
            }
          }
        `;
        return;
      }
      document.body.classList.remove("etb-fgs-print");
      document.getElementById(styleId)?.remove();
    },
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cID || query.cId;
      this.dtFrom = query.dtFrom || "";
      this.dtTo = query.dtTo || "";
      this.cashFlowCategory = query.cashFlowCategory || "";
      this.monthWise =
        query.monthWise === "1" ||
        query.monthWise === 1 ||
        query.monthWise === true;
      this.monthFrom = Number(query.monthFrom) || 0;
      this.monthTo = Number(query.monthTo) || 0;
      this.etbYear = Number(query.year) || new Date().getFullYear();
      if (this.monthFrom > 0 && this.monthTo > 0) {
        this.monthWise = true;
      }

      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      if (!this.db || !this.cID) {
        this.error = "Missing required parameters (db, campusID).";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data.length > 0
        ) {
          // Find the campus that matches cID, fallback to first if cID is not explicitly found or is 0
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];

          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
          this.campusAddress = info.address;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    buildExtendedTrialBalanceApiParams() {
      const params = {
        _db: this.db,
        campusID: this.cID,
      };
      if (this.monthWise && this.monthFrom > 0 && this.monthTo > 0) {
        params.monthFrom = this.monthFrom;
        params.monthTo = this.monthTo;
        params.year = this.etbYear || new Date().getFullYear();
      }
      if (this.cashFlowCategory) {
        params.cashFlowCategory = this.cashFlowCategory;
      }
      return params;
    },

    async prefetchGenerateTrialBalance() {
      if (this.monthWise) return;
      if (!this.dtFrom || !this.dtTo) return;

      let baseUrl = this.$store.state.domain;
      if (!baseUrl.endsWith("/")) baseUrl += "/";

      await axios
        .get(`${baseUrl}TrialBalanceData/GenerateTrialBalance`, {
          params: {
            _db: this.db,
            campusID: this.cID,
            fromDate: this.dtFrom,
            toDate: this.dtTo,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        })
        .catch((error) => {
          console.error("Failed to generate trial balance:", error);
        });
    },

    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        await this.prefetchGenerateTrialBalance();

        let baseUrl = this.$store.state.domain;
        if (!baseUrl.endsWith("/")) baseUrl += "/";

        const url = `${baseUrl}TrialBalanceData/ExtendedTrialBalance`;

        const response = await axios.get(url, {
          params: this.buildExtendedTrialBalanceApiParams(),
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        const payload = this.normalizeExtendedTrialBalanceResponse(
          response.data
        );
        this.monthBlocks = [];
        this.fgsPivotItems = [];
        this.fgsMonthKeys = [];
        this.fgsTotals = null;
        this.fgsSections = [];
        this.fgsGrandTotal = null;
        this.fgsImbalances = [];

        if (payload.pivot) {
          // New myskoolfgs shape: Op.Bal | months[net] | Cl.Bal
          this.monthWise = true;
          this.applyFgsPivotPayload(payload);
          if (!this.fgsPivotItems.length) {
            this.error = "No records found for the selected filters.";
          }
        } else if (payload.monthWise && Array.isArray(payload.data)) {
          this.monthWise = true;
          this.monthBlocks = payload.data.map((block) =>
            this.mapMonthWiseBlock(block)
          );
          this.reportData = [];
          // FGS: convert legacy month-blocks → spreadsheet pivot
          if (this.reportDomain === "myskoolfgs") {
            this.applyFgsPivotFromMonthBlocks(this.monthBlocks);
          }
          const hasRows =
            this.fgsPivotItems.length > 0 ||
            this.monthBlocks.some(
              (b) => this.filterItemsByEtbCategory(b.items).length
            );
          if (!hasRows) {
            this.error = "No records found for the selected filters.";
          }
        } else if (Array.isArray(payload)) {
          this.reportData = payload;
        } else if (payload && Array.isArray(payload.data)) {
          this.reportData = payload.data;
        } else {
          this.reportData = [];
          this.error = "Invalid data format received from server.";
          return;
        }

        if (!this.monthWise && !this.reportData.length) {
          this.error = "No records found for the selected filters.";
        }
      } catch (error) {
        console.error("Failed to load Extended TB:", error);
        this.error = "Network error or server unavailable.";
      } finally {
        this.loading = false;
      }
    },
    normalizeExtendedTrialBalanceResponse(raw) {
      if (!raw) return {};
      const root =
        raw.status === "success" && raw.data != null ? raw : raw;
      const data = root.data;

      // New FGS pivot: accounts with months{ "Jul-25": net }
      const pivotSource = Array.isArray(data)
        ? data
        : Array.isArray(root.items)
        ? root.items
        : Array.isArray(root)
        ? root
        : null;
      const sample = pivotSource && pivotSource[0];
      const isPivotRows =
        sample &&
        sample.months &&
        typeof sample.months === "object" &&
        !Array.isArray(sample.months) &&
        sample.month == null;

      if (isPivotRows || (root.monthWise && Array.isArray(root.monthColumns))) {
        const rows = Array.isArray(pivotSource)
          ? pivotSource
          : Array.isArray(data)
          ? data
          : [];
        return {
          monthWise: true,
          pivot: true,
          data: rows,
          totals: root.totals || raw.totals || null,
          sections: root.sections || raw.sections || [],
          grandTotal: root.grandTotal || raw.grandTotal || null,
          imbalances: root.imbalances || raw.imbalances || [],
          monthKeys:
            root.monthColumns ||
            raw.monthColumns ||
            root.monthKeys ||
            raw.monthKeys ||
            null,
          fromDate: root.fromDate || raw.fromDate || "",
          toDate: root.toDate || raw.toDate || "",
          year: root.year || raw.year || null,
          monthFrom: root.monthFrom || raw.monthFrom || null,
          monthTo: root.monthTo || raw.monthTo || null,
          cashFlowCategory:
            root.cashFlowCategory != null
              ? root.cashFlowCategory
              : raw.cashFlowCategory,
          isBalanced:
            root.isBalanced != null
              ? root.isBalanced
              : raw.isBalanced != null
              ? raw.isBalanced
              : root.grandTotal && root.grandTotal.isBalanced != null
              ? root.grandTotal.isBalanced
              : raw.grandTotal && raw.grandTotal.isBalanced != null
              ? raw.grandTotal.isBalanced
              : root.isMonthColumnsBalanced != null
              ? root.isMonthColumnsBalanced
              : raw.isMonthColumnsBalanced,
        };
      }

      const isMonthBlocks =
        Array.isArray(data) &&
        data.length > 0 &&
        data[0] &&
        data[0].month != null &&
        Array.isArray(data[0].data);
      if ((root.monthWise || isMonthBlocks) && Array.isArray(data)) {
        return { monthWise: true, pivot: false, data };
      }
      if (Array.isArray(data)) {
        return { monthWise: false, data };
      }
      if (Array.isArray(root)) {
        return { monthWise: false, data: root };
      }
      return root;
    },
    mapMonthWiseBlock(block) {
      return {
        month: block.month,
        year: block.year,
        fromDate: block.fromDate,
        toDate: block.toDate,
        items: Array.isArray(block.data) ? block.data : [],
      };
    },
    buildEtbMonthKeys(monthFrom, monthTo, year) {
      const from = Number(monthFrom);
      const to = Number(monthTo);
      const startYear = Number(year) || new Date().getFullYear();
      if (!(from >= 1 && from <= 12 && to >= 1 && to <= 12)) return [];

      const keys = [];
      let m = from;
      let y = startYear;
      // Fiscal wrap e.g. Jul→Jun: monthFrom > monthTo
      const maxSteps = 24;
      for (let i = 0; i < maxSteps; i++) {
        keys.push(`${MONTH_ABBR[m - 1]}-${String(y).slice(-2)}`);
        if (m === to && (from <= to || i > 0)) break;
        m += 1;
        if (m > 12) {
          m = 1;
          y += 1;
        }
        if (from <= to && m > to) break;
      }
      return keys;
    },
    monthKeyFromBlock(block) {
      const m = Number(block.month);
      const y = Number(block.year);
      if (!(m >= 1 && m <= 12) || !y) return null;
      return `${MONTH_ABBR[m - 1]}-${String(y).slice(-2)}`;
    },
    accountPivotKey(item) {
      if (item.accountCode) return String(item.accountCode).trim();
      if (item.accountID != null) return `id-${item.accountID}`;
      if (item.coaID != null) return `coa-${item.coaID}`;
      if (item.coaId != null) return `coa-${item.coaId}`;
      return `${item.nature}|${item.accountTitle}|${item.level}`;
    },
    applyFgsPivotPayload(payload) {
      // API already filters by cashFlowCategory — use rows as returned
      const items = Array.isArray(payload.data) ? payload.data : [];
      const keysFromQuery = this.buildEtbMonthKeys(
        this.monthFrom,
        this.monthTo,
        this.etbYear
      );
      let monthKeys = Array.isArray(payload.monthKeys)
        ? payload.monthKeys.slice()
        : [];
      if (!monthKeys.length && items.length && items[0].months) {
        monthKeys = Object.keys(items[0].months);
      }
      if (!monthKeys.length) monthKeys = keysFromQuery;

      if (payload.year) this.etbYear = Number(payload.year) || this.etbYear;
      if (payload.monthFrom) this.monthFrom = Number(payload.monthFrom) || this.monthFrom;
      if (payload.monthTo) this.monthTo = Number(payload.monthTo) || this.monthTo;
      // Prefer API category when present (bank_account / cash_account)
      if (payload.cashFlowCategory) {
        this.cashFlowCategory = String(payload.cashFlowCategory);
      }

      // Keep API order (sno) — do not regroup/sort
      this.fgsMonthKeys = monthKeys;
      this.fgsPivotItems = items.map((item) => ({
        ...item,
        accountNumber: item.accountNumber || item.accountCode || "",
        months: item.months || {},
      }));
      this.fgsTotals = payload.totals || null;
      this.fgsSections = Array.isArray(payload.sections)
        ? payload.sections
        : [];
      this.fgsGrandTotal = payload.grandTotal || null;
      this.fgsImbalances = Array.isArray(payload.imbalances)
        ? payload.imbalances
        : [];
      this.fgsFromDate = payload.fromDate || "";
      this.fgsToDate = payload.toDate || "";
      // Balanced badge: grandTotal.isBalanced / isBalanced / month columns
      if (payload.isBalanced != null) {
        this.fgsIsBalanced = !!payload.isBalanced;
      } else if (
        payload.grandTotal &&
        payload.grandTotal.isBalanced != null
      ) {
        this.fgsIsBalanced = !!payload.grandTotal.isBalanced;
      } else if (payload.isMonthColumnsBalanced != null) {
        this.fgsIsBalanced = !!payload.isMonthColumnsBalanced;
      } else {
        this.fgsIsBalanced = true;
      }
      this.reportData = [];
    },
    /** Legacy month-blocks → spreadsheet pivot for FGS */
    applyFgsPivotFromMonthBlocks(blocks) {
      const list = Array.isArray(blocks) ? blocks : [];
      if (!list.length) return;

      const monthKeys = list
        .map((b) => this.monthKeyFromBlock(b))
        .filter(Boolean);
      const map = new Map();

      list.forEach((block, bi) => {
        const key = monthKeys[bi];
        if (!key) return;
        (block.items || []).forEach((item) => {
          const ak = this.accountPivotKey(item);
          if (!map.has(ak)) {
            map.set(ak, {
              ...item,
              openingDebit: 0,
              openingCredit: 0,
              closingDebit: 0,
              closingCredit: 0,
              months: {},
            });
          }
          const row = map.get(ak);
          if (bi === 0) {
            row.openingDebit = Number(item.openingDebit) || 0;
            row.openingCredit = Number(item.openingCredit) || 0;
          }
          if (bi === list.length - 1) {
            row.closingDebit = Number(item.closingDebit) || 0;
            row.closingCredit = Number(item.closingCredit) || 0;
            row.accountTitle = item.accountTitle || row.accountTitle;
            row.accountCode = item.accountCode || row.accountCode;
            row.accountID = item.accountID || row.accountID;
            row.nature = item.nature || row.nature;
            row.level = item.level != null ? item.level : row.level;
            row.isParent = item.isParent;
          }
          const net =
            (Number(item.activityDebit) || 0) -
            (Number(item.activityCredit) || 0);
          row.months[key] = net;
        });
      });

      const items = this.filterItemsByEtbCategory(Array.from(map.values()));
      this.fgsMonthKeys = monthKeys;
      this.fgsPivotItems = items;
      this.fgsTotals = null;
      this.fgsImbalances = [];
    },
    itemMatchesEtbCategory(item) {
      if (!this.cashFlowCategory) return true;
      const raw = String(item?.cashFlowCategory ?? "")
        .trim()
        .toLowerCase();
      if (this.cashFlowCategory === "bank_account") {
        return (
          raw === "1" ||
          raw === "bank" ||
          raw === "bank_account" ||
          raw === "bank account"
        );
      }
      if (this.cashFlowCategory === "cash_account") {
        return (
          raw === "0" ||
          raw === "cash" ||
          raw === "cash_account" ||
          raw === "cash account"
        );
      }
      return true;
    },
    filterItemsByEtbCategory(items) {
      const list = Array.isArray(items) ? items : [];
      if (!this.cashFlowCategory) return list;
      return list.filter((item) => this.itemMatchesEtbCategory(item));
    },
    formatNumber(value) {
      const num = Number(value);
      return Number.isFinite(num)
        ? new Intl.NumberFormat("en-PK").format(num)
        : "0";
    },
    getRowClass(item) {
      if (item.level === 1) return "level-1-row";
      if (item.level === 2) return "level-2-row";
      return "item-row";
    },
    groupItemsByNature(items) {
      const list = Array.isArray(items) ? items : [];
      if (!list.length) return {};

      const groups = {};
      const natureTemplate = [
        "ASSET",
        "EXPENSE",
        "LIABILITY",
        "EQUITY",
        "REVENUE",
      ];

      list.forEach((item) => {
        const nature = this.normalizeEtbNatureKey(item.nature);

        if (!groups[nature]) groups[nature] = [];
        groups[nature].push(item);
      });

      Object.keys(groups).forEach((nature) => {
        groups[nature].sort((a, b) =>
          (a.accountCode || "").localeCompare(b.accountCode || "", undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
      });

      const sortedGroups = {};
      natureTemplate.forEach((n) => {
        if (groups[n]) sortedGroups[n] = groups[n];
      });
      Object.keys(groups).forEach((key) => {
        if (!natureTemplate.includes(key)) sortedGroups[key] = groups[key];
      });

      return sortedGroups;
    },
    getNatureTotal(nature, field, items) {
      const grouped = this.groupItemsByNature(items);
      return (
        grouped[nature]?.reduce((sum, item) => {
          if (!item.isParent) {
            return sum + (parseFloat(item[field]) || 0);
          }
          return sum;
        }, 0) || 0
      );
    },
    totalGrand(field, items) {
      const grouped = this.groupItemsByNature(items);
      return Object.keys(grouped).reduce((sum, nature) => {
        return sum + this.getNatureTotal(nature, field, items);
      }, 0);
    },
    formatEtbMonthLabel(month) {
      const names = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const m = Number(month);
      return m >= 1 && m <= 12 ? names[m - 1] : `Month ${month}`;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    normalizeEtbNatureKey(nature) {
      let n = String(nature || "OTHER").toUpperCase();
      if (n === "ASSETS") n = "ASSET";
      if (n === "LIABILITIES") n = "LIABILITY";
      if (n === "EXPENSES") n = "EXPENSE";
      if (n === "REVENUES") n = "REVENUE";
      if (n === "EQUITIES") n = "EQUITY";
      return n;
    },
    formatAmount(value) {
      const num = Number(value);
      if (!Number.isFinite(num) || num === 0) return "-";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    },
    formatCurrency(value) {
      return this.formatAmount(value);
    },
    openLedger(item, section) {
      const dtFrom =
        (section && section.fromDate) || this.dtFrom || "";
      const dtTo = (section && section.toDate) || this.dtTo || "";
      const query = {
        db: this.db,
        cID: this.cID,
        accountID: item.accountID,
        dtFrom,
        dtTo,
      };
      // FGS bank/cash ETB → AdvanceAccountsLedger category (BPV/BRV or CPV/CRV)
      if (this.isFgsPrintMode && this.fgsLedgerCategory) {
        query.category = this.fgsLedgerCategory;
      }
      const routeData = this.$router.resolve({
        name: "advance-ledger-print",
        query,
      });
      window.open(routeData.href, "_blank");
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.extended-trial-balance-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
  overflow-x: hidden;
  box-sizing: border-box;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);
  max-width: 1750px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 4rem 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 100%;
  max-width: 1750px;
  margin: 30px auto;
  transition: all 0.3s ease;
  overflow: hidden;
  box-sizing: border-box;
}

.etb-month-section {
  margin-top: 2rem;
}

.etb-month-banner {
  padding: 1rem 1.25rem;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    rgba(115, 103, 240, 0.1) 0%,
    rgba(115, 103, 240, 0.04) 100%
  );
  border: 1px solid rgba(115, 103, 240, 0.15);
}

.etb-month-banner__title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #4b4b4b;
}

.etb-month-banner__range {
  font-size: 0.95rem;
  color: #6e6b7b;
  margin-top: 0.35rem;
}

.etb-month-banner__meta {
  display: block;
  margin-top: 0.35rem;
}

.etb-empty-month {
  border: 1px dashed #dcdcdc;
  border-radius: 8px;
  background: #fafafa;
}

.etb-fgs-layout {
  max-width: 100% !important;
  width: 100% !important;
  padding: 2rem 1.25rem !important;
  margin: 12px auto !important;
  overflow: visible !important;
  box-shadow: none !important;
}

.etb-fgs-print-report {
  padding: 1rem 0.75rem !important;
  max-width: 100% !important;

  .control-bar {
    max-width: 100% !important;
  }
}

.etb-fgs-chunk {
  margin-bottom: 0.75rem;

  & + .etb-fgs-chunk {
    margin-top: 0.5rem;
    padding-top: 0;
    border-top: none;
  }
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .d-flex {
    min-width: 0;
  }

  .header-text-block {
    min-width: 0;
    flex: 1;
  }

  .school-logo-img {
    height: 90px;
    width: auto;
    max-width: 120px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .school-name-full {
    font-size: 1.7rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 0;
      flex-shrink: 0;
    }

    .date-range-text {
      color: #555;
      min-width: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  }
}

.etb-table-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 2rem;
}

.etb-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
  border: 1px solid #dee2e6;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 8px 10px;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
    vertical-align: top;
  }

  .col-account {
    width: 30%;
  }
  .col-group {
    width: 23.33%;
  }
  .col-amount {
    width: 11.67%;
    white-space: nowrap;
  }

  thead {
    .main-header {
      background: #2c3e50;
      th {
        color: white;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-weight: 700;
        white-space: normal;
        vertical-align: middle;
      }
    }
    .sub-header {
      background: #f8f9fa;
      th {
        font-size: 0.8rem;
        font-weight: 700;
        color: #636e72;
      }
    }
  }

  .nature-header {
    background: #e9ecef;
    td {
      font-weight: 800;
      font-size: 1.1rem;
      color: #2c3e50;
      padding: 15px;
      text-transform: uppercase;
    }
  }

  .account-cell {
    position: relative;
    border-right: 1px solid #eee;
    overflow-wrap: anywhere;
    word-break: break-word;

    .account-link {
      color: #000;
      text-decoration: none;
      transition: all 0.2s;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      max-width: 100%;
      min-width: 0;

      .account-code {
        color: #000;
        font-family: "JetBrains Mono", "Courier New", monospace;
        font-size: 0.78rem;
        background: transparent;
        padding: 0;
        margin-bottom: 2px;
        font-weight: 700;
        flex-shrink: 0;
        white-space: normal;
        word-break: break-all;
        opacity: 0.5;
      }

      .account-title {
        font-weight: 500;
        font-size: 0.95rem;
        padding-top: 0;
        line-height: 1.3;
        word-wrap: break-word;
        overflow-wrap: anywhere;
        word-break: break-word;
        max-width: 100%;
      }

      &:hover {
        color: #2e86de;
        .account-title {
          text-decoration: underline;
        }
      }
    }

    &.lvl-1 {
      background: #f1f3f5;
    }
    &.lvl-2 {
      background: #f8f9fa;
    }

    &.is-parent {
      .account-title {
        font-weight: 800 !important;
        text-transform: uppercase;
        font-size: 1rem;
        letter-spacing: 0.2px;
      }
    }

    .tree-line {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 2px;
      background: #adb5bd;

      &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 0;
        width: 12px;
        height: 2px;
        background: #adb5bd;
      }
    }
  }

  .level-1-row {
    background: #f1f3f5;
    border-top: 2px solid #222;
    td {
      font-weight: 900;
      color: #222;
      font-size: 1.05rem;
    }
  }

  .level-2-row {
    background: #f8f9fa;
    border-top: 1px solid #dee2e6;
    td {
      font-weight: 700;
      color: #333;
    }
  }

  .item-row {
    border-bottom: 1px solid #f1f2f6;
    &:hover {
      background: #fdfdfd;
    }
  }

  .amount-cell {
    font-family: "Outfit", sans-serif;
    color: #2d3436;
    font-weight: 700;
    padding: 6px 10px;
    border-right: 1px solid #f1f2f6;
    font-size: 0.88rem;
    white-space: nowrap;

    &.font-weight-bold {
      font-weight: 800;
      color: #000;
    }
  }

  .nature-footer {
    background: #f1f2f6;
    td {
      border-top: 2px solid #2c3e50;
      font-weight: 800;
      color: #2c3e50;
      font-size: 1.05rem;
    }
  }

  .grand-total-row {
    background: #2c3e50;
    td {
      color: white;
      font-size: 1.1rem;
      padding: 18px;
    }
    .total-highlight {
      background: #e17055;
    }
  }
}

.signature-section {
  display: flex;
  justify-content: space-between;
  margin-top: 5rem;

  .sig-box {
    text-align: center;
    width: 250px;

    .sig-line {
      border-top: 1px solid #1a1a1a;
      margin-bottom: 0.5rem;
    }

    span {
      font-weight: 600;
      color: #636e72;
      font-size: 0.9rem;
    }
  }
}

.bg-opening {
  background-color: rgba(9, 132, 227, 0.05);
}
.bg-activity {
  background-color: rgba(0, 184, 148, 0.05);
}
.bg-closing {
  background-color: rgba(108, 92, 231, 0.05);
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .extended-trial-balance-report {
    background: white !important;
    padding: 0 !important;
    margin: 0 !important;
    min-height: auto !important;
    height: auto !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1cm 1.5cm !important; /* Internal margins for print */
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    min-height: auto !important;
    overflow: visible !important;
    page-break-after: auto;
  }

  .etb-month-section {
    page-break-before: always;
    break-before: page;
  }

  .etb-fgs-chunk {
    page-break-inside: auto;
    break-inside: auto;
    margin-bottom: 0.35cm !important;
  }

  .etb-fgs-layout {
    padding: 0.4cm 0.45cm !important;
  }

  .etb-fgs-print-report .report-header-modern {
    margin-bottom: 0.25cm !important;
    padding-bottom: 0.2cm !important;
    page-break-after: avoid;
  }

  ::v-deep .etb-fgs-table-block {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    margin-bottom: 0.35cm !important;
    overflow: visible !important;
    max-width: 100% !important;
  }

  ::v-deep .etb-fgs-account-cell,
  ::v-deep .etb-fgs-th-account {
    overflow: hidden !important;
    text-overflow: ellipsis;
  }

  ::v-deep .etb-fgs-account-cell .etb-fgs-account-link {
    color: #2d3436 !important;
  }

  ::v-deep .etb-fgs-account-cell .etb-fgs-account-title {
    color: #2d3436 !important;
  }

  .report-page.etb-fgs-layout {
    overflow: visible !important;
  }

  ::v-deep .etb-fgs-unified-table {
    width: 100% !important;
    max-width: 100% !important;
    font-size: 7pt !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;

    thead {
      display: table-header-group;
    }

    .etb-fgs-data-row {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .etb-fgs-nature-header {
      page-break-after: avoid;
    }

    .etb-fgs-col-amount,
    .etb-fgs-amount-cell {
      min-width: 0 !important;
      max-width: none !important;
      padding: 2px 4px !important;
    }

    .etb-fgs-col-account,
    .etb-fgs-account-cell {
      width: 130px !important;
      max-width: 130px !important;
      padding: 2px 6px !important;
    }

    .etb-fgs-account-title {
      font-size: 6.5pt !important;
    }

    .etb-fgs-account-code {
      font-size: 6pt !important;
    }

    .etb-fgs-amount-value {
      font-size: 7pt !important;
      line-height: 1.2 !important;
      white-space: nowrap !important;
      overflow-wrap: normal !important;
    }

    .etb-fgs-main-header .etb-fgs-th-account,
    .etb-fgs-main-header .etb-fgs-th-month {
      background: #2c3e50 !important;
      color: #ffffff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .etb-fgs-main-header .etb-fgs-month-title {
      color: #ffffff !important;
    }

    .etb-fgs-main-header .etb-fgs-month-year {
      color: rgba(255, 255, 255, 0.92) !important;
    }

    .etb-fgs-bg-opening,
    .etb-fgs-bg-activity,
    .etb-fgs-bg-closing {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .etb-table-wrap {
    overflow: visible !important;
    margin-top: 1rem !important;
  }

  .etb-table {
    font-size: 9pt !important;
    table-layout: fixed !important;
    width: 100% !important;

    th,
    td {
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      word-break: break-word !important;
      white-space: normal !important;
    }

    th,
    td:not(.account-cell) {
      padding: 4px 8px !important;
    }

    .col-amount,
    td.amount-cell {
      white-space: nowrap !important;
    }

    td.account-cell {
      padding-top: 4px !important;
      padding-bottom: 4px !important;
      padding-right: 8px !important;
      /* padding-left is handled by inline style for tree indentation */
    }

    .nature-header td {
      padding: 6px 8px !important;
      font-size: 1rem !important;
    }

    thead .main-header {
      background: #f1f2f6 !important;
      th {
        color: #000 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }

    .nature-header {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .grand-total-row {
      background: #e9ecef !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      td {
        color: #000 !important;
        padding: 8px 10px !important;
      }
    }

    .total-highlight {
      background: #fff !important;
      color: #000 !important;
    }
  }

  .report-header-modern {
    margin-bottom: 5px !important;
    padding-bottom: 5px !important;

    .school-logo-img {
      height: 60px !important;
    }

    .school-name-full {
      font-size: 1.4rem !important;
    }

    .report-info-line {
      font-size: 0.9rem !important;
    }
  }

  .signature-section {
    margin-top: 30px !important;
    page-break-inside: avoid;
  }

  .page-footer {
    display: none;
  }

  .etb-fgs-print-report {
    width: 100% !important;
    max-width: none !important;
  }
}
</style>

<!-- Non-FGS ETB print page (FGS uses injected Tabloid @page in applyFgsPrintDocumentMode) -->
<style lang="scss">
@page etb-default-landscape {
  size: landscape;
  margin: 10mm 12mm;
}

@media print {
  .report-page:not(.etb-fgs-layout) {
    page: etb-default-landscape;
  }
}
</style>
